// Imports
// ********************************************
import {
  useSwrApi, useOnTabRefocus, ApiHeaderInfoOk,
} from '@hooks/useSwrApi';
import { useAppDispatch, useAppSelector } from 'src/app';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { useSession, signOut } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

// Dev-only guard: this hook is the single fetching owner of `/v1/header-info` and must be mounted
// exactly once (in the Header). It used to be called per job card, and because its refocus handler
// calls a bound `mutate()` — which deliberately bypasses SWR deduping — every tab refocus fired one
// request per instance (24+ on a job list). Readers must use `useHeaderInfo`, refreshers
// `useHeaderInfoRefresh` (both in `@hooks/features/useHeaderInfo`).
let mountedInstances = 0;

/**
 * @description Hook that fetches header info data and also saves it in Redux state for other uses,
 * even though a subscription to the same key (that means the fetch url) would be the same thing (you
 * access the swr cache). Uses NextAuth session to determine authentication status.
 *
 * How it works:
 * 1. We check if the user is authenticated using NextAuth session
 * 2. If yes, we subscribe (fetch) to the header info data. When not authenticated, the swr cache is
 *    emptied (mutated) and the data is cleared from Redux state.
 * 3. We also repeat the process on tab refocus (visibility change) and revalidate (re-fetch) the
 *    swr cache if the conditions inside the 'revalidateHeaderInfo' function are met. This guarantees that
 *    the data is always fresh, no matter if the user is authenticated or not and how many browser tabs
 *    are open.
 * 4. We can force check for the auth status. If the authentication is valid, then it will re-fetch the
 *    swr data.
 *
 * Authentication detection:
 * - Uses NextAuth useSession hook to check authentication status
 * - Falls back to API check if session status is loading
 */
export const useHeaderInfoData = (locale = 'ro', fetchOnMount = true) => {
  // redux dispatch
  const appDispatch = useAppDispatch();
  const headerInfo = useAppSelector(getUserHeaderInfo);

  // NextAuth session
  const { data: session, status, update: updateSession } = useSession();
  const isAuthenticated = status === 'authenticated' && !!session;

  // Warn if a second instance mounts — see `mountedInstances` above. Dev only; StrictMode's
  // double-mount is accounted for by the cleanup, so a genuine duplicate is what surfaces here.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      mountedInstances += 1;

      if (mountedInstances > 1) {
        console.warn(
          `[useHeaderInfoData] ${mountedInstances} instances mounted. This hook owns the /v1/header-info `
          + 'subscription and belongs only in the Header. Read with useHeaderInfo() or refresh with '
          + 'useHeaderInfoRefresh() from @hooks/features/useHeaderInfo instead.',
        );
      }

      return () => {
        mountedInstances -= 1;
      };
    }

    return undefined;
  }, []);

  // The `hasBusinessWallet` self-heal below must run at most once per mount: when `token.headerInfo`
  // is absent the jwt callback (auth.ts) skips the patch silently, so the live value never matches
  // the session and every successful fetch would fire another updateSession().
  const didAttemptWalletHeal = useRef(false);

  // Determine if user is authenticated
  // Use existing headerInfo as a hint to prevent flickering during initial load
  // If session is loading, optimistically use headerInfo if it exists
  const userAuthenticated = isAuthenticated || (status === 'loading' && fetchOnMount && !!headerInfo);



  // SWR: fetch data conditionally based on cookie presence
  // ********************************************
  const {
    error, isLoading, isValidating, mutate,
  } = useSwrApi<ApiHeaderInfoOk>({
    url: '/v1/header-info',
    shouldFetch: userAuthenticated,
    locale,
    apiOptions: {
      revalidateOnFocus: false,
      // On success, save the data in Redux state if it's valid
      onSuccess: (newData) => {
        // save header info in Redux state
        appDispatch({ type: 'user/saveHeaderInfo', payload: newData });

        // Self-heal the session: the JWT freezes `hasBusinessWallet` at login, and it can be
        // stale for some company accounts — which mis-routes the /my-jobs hub to /applied-jobs.
        // When the live value diverges, push it into the JWT (one update; later fetches match
        // and no-op). The jwt callback in auth.ts applies the patch on trigger === 'update'.
        const sessionWallet = (session?.user as { hasBusinessWallet?: boolean } | undefined)?.hasBusinessWallet;
        if (
          !didAttemptWalletHeal.current
          && typeof newData.hasBusinessWallet === 'boolean'
          && sessionWallet !== newData.hasBusinessWallet
        ) {
          didAttemptWalletHeal.current = true;
          void updateSession({ hasBusinessWallet: newData.hasBusinessWallet });
        }
      },
      // On error, clear the data from Redux state
      // 401 means user is not authenticated (no valid session)
      // Other errors might be temporary, but we still clear state to be safe
      onError: (errorData) => {
        appDispatch({ type: 'user/clearHeaderInfo' });

        if (!errorData || !errorData.errors?.length) {
          return;
        }

        const errorCode = errorData.errors[0].code;

        // For /v1/header-info a 401 error means user is not authenticated
        if (errorCode === 401) {
          // Sign out from NextAuth and redirect to home page
          void signOut({ redirect: false }).then(() => {
            window.location.href = locale === 'ro' ? '/' : `/${locale}`;
          });
        }
      },
    },
  });


  // Run the callback on tab refocus. Check NextAuth session status.
  // ********************************************
  useOnTabRefocus(() => {
    // Session status will be automatically updated by useSession
    // This is mainly for triggering a revalidation if needed
    if (isAuthenticated) {
      void mutate();
    }
  });

  // Force check for the auth status and revalidate if authenticated.
  // Uses NextAuth session to determine authentication status.
  // If session says not authenticated, manually refetch the session to check for updates.
  // ********************************************
  const forceCheckAuthStatus = useCallback(async () => {
    if (isAuthenticated) {
      void mutate();
    } else {
      try {
        await updateSession();
        void mutate();
      } catch {
        // Ignore errors
      }
    }
  }, [isAuthenticated, mutate, updateSession]);

  // Return data
  // ********************************************
  return useMemo(() => ({
    headerInfoData: headerInfo,
    headerInfoError: error,
    headerInfoLoading: isLoading,
    headerInfoValidating: isValidating,
    mutateHeaderInfo: mutate,
    forceCheckAuthStatus,
    isSessionAuthenticated: isAuthenticated,
    isSessionLoading: status === 'loading',
  }), [headerInfo, error, isLoading, isValidating, mutate, forceCheckAuthStatus, isAuthenticated, status]);
};
